import React, {useState, useCallback, useEffect, useRef} from 'react';
import {Map, GoogleApiWrapper, Marker} from 'google-maps-react';
import Autocomplete from 'react-google-autocomplete';
import Geocode from 'react-geocode';
import '../../../GatewaysMap/index.css';
import {useEditorDimensions} from '../../../../utils/hooks';
import config from '../../../../conf/config';
import { makeStyles } from '@mui/styles';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import gwStaticMapIcon from '../../../../assets/images/maps/GWstatic.png';
import {GatewayGetSchema} from '../../../../api/DBSchema/Gateway';
import {hasValue} from '../../../../utils/utils';
import {useTranslation} from 'react-i18next';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import SMCLink from '../../../../components/Simple/SMCLink';
import EmptyRow from '../../../../components/Simple/EmptyRow';
import SMCPageLoading from '../../../../components/core/pageComponents/SMCPageLoading';

const pageSchema = GatewayGetSchema.pages.location;
const pageId = pageSchema.pageId;

Geocode.setApiKey(config.google_maps_api_key);

function GatewayLocation({
    google,
    gatewayObject,
    setGatewayData
}) {
    const { t } = useTranslation();
    const {editorWidth, editorHeight} = useEditorDimensions();
	const classes = useStyles({ height: editorHeight-90, width: editorWidth-300 });

    const pageData = gatewayObject[pageId];
    const lat = pageData[pageSchema.lat];
    const lng = pageData[pageSchema.lng];
    const isLocationSet = (hasValue(lat) && hasValue(lng));

    const setLocationData = useCallback(location => {
        setGatewayData([pageId, pageSchema.lat], location.lat);
        setGatewayData([pageId, pageSchema.lng], location.lng);
    }, [setGatewayData])

    const markerPosition = isLocationSet ? {lat,lng} : null;

    const [bounds, setBounds] = useState(google ? new google.maps.LatLngBounds() : undefined);
    const [mapCenter, setMapCenter] = useState(markerPosition);
    const [mapZoom, setMapZoom] = useState(isLocationSet ? 12 : 2);
    const [markerAddress, setMarkerAddress] = useState();
    
    const [autocompleteSearchString, setAutocompleteSearchString] = useState('');
    const [autocompleteKey, setAutocompleteKey] = useState(0);
    
    useEffect(() => {
            Geocode.fromLatLng(lat, lng)
            .then(resp => setMarkerAddress(resp.results[0].formatted_address))
            .catch(e => setMarkerAddress(null));
    }, [lat, lng]);

    // Change autocomplete key on bounds change to force rerender of Autocomplete
    // (Autocomplete updates bounds on render only)
    useEffect(() => {
        setAutocompleteKey(key => key + 1);
    }, [bounds]);

    const autocompleteHandleKeyDown = useCallback(e => {
        if (e.key === 'Enter') {
            const searchValue = e.target.value;
            const service = new google.maps.places.AutocompleteService();
            service.getPlacePredictions({
                input: searchValue,
                bounds: bounds,
                types: ['geocode', 'establishment']
            }, predictions => {
                if (predictions[0]) {
                    const geocoder = new google.maps.Geocoder();
                    geocoder.geocode({ 
                        'placeId': predictions[0].place_id
                    }, 
                    (responses, status) => {
                        if (status === 'OK') {
                            const lat = responses[0].geometry.location.lat();
                            const lng = responses[0].geometry.location.lng();
                            setLocationData({lat, lng});
                            setMapCenter({lat, lng});
                            setMapZoom(20);
                            setAutocompleteSearchString(predictions[0].description);
                        }
                    });            
                }
            })
        }
    }, [bounds, setLocationData, google]);

    const lastLocationInfoWindow = useRef(null);

	const setToCurrentLocation = useCallback((map) => {
		if (lastLocationInfoWindow.current) {
			lastLocationInfoWindow.current.close();
			lastLocationInfoWindow.current = null;
		}
		// Try HTML5 geolocation
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const pos = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					};
					map.setCenter(pos);
					map.setZoom(12);
				},
				() => {
					const infoWindow = new google.maps.InfoWindow();
					infoWindow.setPosition(map.getCenter());
					infoWindow.setContent("Error: The Geolocation service failed.");
					lastLocationInfoWindow.current = infoWindow;
					infoWindow.open(map);
				}
			);
		} else {
			// Browser doesn't support Geolocation
			const infoWindow = new google.maps.InfoWindow();
			infoWindow.setPosition(map.getCenter());
			infoWindow.setContent("Error: Your browser doesn't support geolocation.");
			lastLocationInfoWindow.current = infoWindow;
			infoWindow.open(map);
		}
	}, [google]);

    return (
        <EditorLayout>
            {
                !isLocationSet && (
                    <SMCLabel>{t('SMP.GATEWAYS.PAGE.LOCATION.LINE1')}</SMCLabel>
                )
            }
            {
                isLocationSet && (
                    <SMCLabel>You have set the gateway's location manually. <SMCLink inline onClick={() => setLocationData({lng: null, lat: null})}>Clear location</SMCLink></SMCLabel>
                )
            }
            { markerAddress ? markerAddress : <EmptyRow /> }
            {
                !google && (
                    <div>
                        Error loading google maps, check your internet connection and try again...
                    </div>
                )
            }
            {
                google && (
                    <div className={classes.root}>
                        <div id="filterBox" style={{zIndex: 3, position: 'absolute', left: '20px'}}>
                            <Autocomplete
                                id="pac-input"
                                className="controls"
                                placeholder={t('SMP.GOOGLE_MAPS_CONTAINER.FILTER_BOX.PAC_INPUT')}
                                onPlaceSelected={place => {
                                    if (place.geometry) {
                                        const lat = place.geometry.location.lat();
                                        const lng = place.geometry.location.lng();
                                        setLocationData({lat, lng});
                                        setMapCenter({lat, lng});
                                        setMapZoom(20);
                                    }
                                }}
                                bounds={bounds} // Key needs to be changed on bounds change
                                key={autocompleteKey} // On key change, this triggers a rerender, which will update the bounds
                                value={autocompleteSearchString}
                                onChange={e => setAutocompleteSearchString(e.target.value)}
                                types={['geocode', 'establishment']}
                                onKeyDown={autocompleteHandleKeyDown}
                            />
                        </div>
                        <Map
                            google={google}
                            mapTypeControl={false}
                            center={mapCenter}
                            zoom={mapZoom}
							onZoomChanged={(mapProps, map, e) => setMapZoom(map.getZoom())}
                            maxZoom={18}
                            initialCenter={isLocationSet ? markerPosition : {lat: 23.2060113, lng: -17.6077523}}
                            onBoundsChanged={(mapProps, map, e) => setBounds(map.getBounds())}
                            onClick={(mapProps, map, e) => setLocationData({lat: e.latLng.lat(), lng: e.latLng.lng()})}
                            onReady={(markerProps, map, e) => {
								const locationButton = document.createElement("button");
								locationButton.textContent = "Pan to current location";
								locationButton.id = "location-button";
								locationButton.addEventListener("click", () => setToCurrentLocation(map));
								map.controls[google.maps.ControlPosition.TOP_CENTER].push(locationButton);
							}}
                        >
                            {
                                isLocationSet && (
                                    <Marker
                                        position={markerPosition}
                                        icon={gwStaticMapIcon}
                                        draggable={true}
                                        onDragend={(markerProps, map, e) => setLocationData({lat: e.latLng.lat(), lng: e.latLng.lng()})}
                                    />
                                )
                            }
                        </Map>
                    </div>
                )
            }
        </EditorLayout>
    );
}

const useStyles = makeStyles({
    root: {
        width: props => props.width,
        height: props => props.height,
        position: 'relative'
    }
});

export default GoogleApiWrapper({
    client: config.google_maps_api_client,
    LoadingContainer: SMCPageLoading
})(GatewayLocation);